// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The layout options the page lays a site out with, in one place, so the dev
 * renderer and any test that wants "the drawing as the app draws it" get the
 * same node height, widths and ports rather than a copy that drifts.
 */

import { SYMBOL_NODE_SIZE, isSymbolNode } from '../../../config/powerFlowSymbols';
import type { PowerFlowLayoutOptions } from '../../../utils/powerFlowLayout';
import { measureDeviceWidth, measureGroupWidth } from './measureNodeWidth';

/**
 * Node height on the page: room for the measured and inferred lines under the
 * label. A structural constant, so it never changes on a poll.
 */
export const PAGE_NODE_HEIGHT = 62;

export const deviceLayoutOptions = ({
  labelOf,
  groupLabelOf,
}: {
  /** Short name for an asset — also the off-sheet stub's text. */
  labelOf: (_id: string) => string;
  /** Display type for a load group's members, e.g. `IT Rack`. */
  groupLabelOf: (_entityType: string) => string;
}): Partial<PowerFlowLayoutOptions> => ({
  nodeHeight: PAGE_NODE_HEIGHT,
  labelOf,
  // Chips are sized to their text. Without this every name on a real site
  // elides, and an elided chip says nothing at all.
  widthOf: (id, { group, vertex }) =>
    group
      ? measureGroupWidth(groupLabelOf(group.entityType), group.memberIds.length)
      : measureDeviceWidth(labelOf(id), vertex.symbol),
  // A device drawn as its symbol takes its conductors on the symbol, at the
  // node's left, not on the middle of its text.
  portXOf: (_id, { group, vertex }) =>
    !group && isSymbolNode(vertex.symbol) ? SYMBOL_NODE_SIZE / 2 : undefined,
});
